import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, List, ListItem, ListItemText, TextField, Button, Card, CardContent, CardMedia, Input, ListItemIcon, FormControl, Select, MenuItem, InputAdornment, InputLabel, IconButton, Tooltip, Collapse, Tabs, Tab, Dialog, DialogTitle, DialogContent, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LocationSelector from '../components/LocationSelector';
import bazarBn from '../i18n/bn/bazar';
import bazarEn from '../i18n/en/bazar';
import bazarNp from '../i18n/np/bazar';
import bazarPk from '../i18n/pk/bazar';
import bazarBt from '../i18n/bt/bazar';
import bazarSl from '../i18n/sl/bazar';
import bazarDe from '../i18n/de/bazar';
import bazarAf from '../i18n/af/bazar';
import bazarCn from '../i18n/cn/bazar';
import { 
  LocalFlorist,
  LocalGroceryStore, 
  Icecream, 
  ShoppingCart, 
  LocalCafe, 
  CleaningServices, 
  Fastfood, 
  Whatshot, 
  SetMeal, 
  Grass,
  Toys,
  Search,
  LocalMall,
  Store,
  LocationOn,
  Close,
  Upload,
  ExpandLess,
  ExpandMore,
  Nature,
  Spa,
  Healing,
  Park
} from '@mui/icons-material';

import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { formatPriceWithBengaliDigits } from '../utils/priceUtils';

// Add keyframes for cursor blinking
const keyframes = `
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

// Inject the keyframes into the document
const injectGlobalStyles = () => {
  const styleEl = document.createElement('style');
  styleEl.innerHTML = keyframes;
  document.head.appendChild(styleEl);
  return () => document.head.removeChild(styleEl);
};

// Styled components
// Styled components
const Sidebar = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  maxWidth: '260px',
  width: '100%',
}));

const CategoryList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const StoreList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
  color: 'white',
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

const MainContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const GroceryCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  width: '100%',
  margin: '0 auto',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: '4px 8px',
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 'bold',
}));

const SearchBarHeader = styled(Paper)(({ theme }) => ({
  position: 'relative',
  minHeight: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'nowrap',
  gap: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  overflow: 'hidden',
  zIndex: 1,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
}));

const VideoBackground = styled('video')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: -1,
});

const SearchOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(45deg, rgba(94, 53, 177, 0.82) 30%, rgba(30, 136, 229, 0.72) 90%)',
  zIndex: -1,
}));

// Default logo component for grocery stores
const DefaultStoreLogo = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  backgroundColor: '#e3f2fd',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #bbdefb',
}));

// Districts list
const districts = [
  { bn: 'সকল জেলা', en: 'All Districts' },
  { bn: 'ঢাকা', en: 'Dhaka' },
  { bn: 'চট্টগ্রাম', en: 'Chattogram' },
  { bn: 'সিলেট', en: 'Sylhet' },
  { bn: 'খুলনা', en: 'Khulna' },
  { bn: 'রাজশাহী', en: 'Rajshahi' },
  { bn: 'বরিশাল', en: 'Barishal' },
  { bn: 'রংপুর', en: 'Rangpur' },
  { bn: 'ময়মনসিংহ', en: 'Mymensingh' }
];

// All upazillas list
const allUpazillas = [
  { bn: 'সকল উপজেলা', en: 'All Upazillas' },
  { bn: 'ধানমন্ডি', en: 'Dhanmondi' },
  { bn: 'মিরপুর', en: 'Mirpur' },
  { bn: 'গুলশান', en: 'Gulshan' },
  { bn: 'উত্তরা', en: 'Uttara' },
  { bn: 'মোহাম্মদপুর', en: 'Mohammadpur' },
  { bn: 'বনানী', en: 'Banani' },
  { bn: 'সিলেট সদর', en: 'Sylhet Sadar' },
  { bn: 'চট্টগ্রাম সদর', en: 'Chattogram Sadar' },
  { bn: 'খুলনা সদর', en: 'Khulna Sadar' },
  { bn: 'রাজশাহী সদর', en: 'Rajshahi Sadar' }
];

// List of bazars
const bazars = [
  { bn: 'সকল বাজার', en: 'All Bazars' },
  { bn: 'চাচকৈড় বাজার', en: 'Chachkoir Bazar' },
  { bn: 'খুবজিপুর বাজার', en: 'Khubipur Bazar' },
  { bn: 'নাজিরপুর বাজার', en: 'Nazirpur Bazar' },
  { bn: 'বিলদোহর বাজার', en: 'Bildohar Bazar' },
  { bn: 'চাটখিল বাজার', en: 'Chatkhil Bazar' },
  { bn: 'সোনাইমুড়ি বাজার', en: 'Sonaimuri Bazar' },
  { bn: 'কেন্দ্রীয় বাজার', en: 'Central Bazar' },
  { bn: 'নিউ মার্কেট', en: 'New Market' },
  { bn: 'গুলিস্তান বাজার', en: 'Gulistan Bazar' },
  { bn: 'মৌচাক বাজার', en: 'Mouchak Bazar' },
  { bn: 'রকেট বাজার', en: 'Rocket Bazar' },
  { bn: 'কাওরান বাজার', en: 'Kawran Bazar' },
  { bn: 'ফার্মগেট বাজার', en: 'Farmgate Bazar' },
  { bn: 'মিরপুর বাজার', en: 'Mirpur Bazar' },
  { bn: 'উত্তরা বাজার', en: 'Uttara Bazar' },
  { bn: 'ধানমন্ডি বাজার', en: 'Dhanmondi Bazar' },
  { bn: 'মোহাম্মদপুর বাজার', en: 'Mohammadpur Bazar' },
  { bn: 'বনানী বাজার', en: 'Banani Bazar' },
  { bn: 'গুলশান বাজার', en: 'Gulshan Bazar' }
];

// Grocery categories with hierarchical structure like FoodBazar
const groceries = [
  {
    category: { bangla: "তেল ও ঘি", english: "Oils & Ghee" },
    icon: <LocalGroceryStore />,
    items: [
      { bangla: "সরিষার তেল", english: "Mustard Oil", icon: <LocalGroceryStore /> },
      { bangla: "সয়াবিন তেল", english: "Soybean Oil", icon: <LocalGroceryStore /> },
      { bangla: "রান্নার তেল", english: "Cooking Oil", icon: <LocalGroceryStore /> },
      { bangla: "ঘি", english: "Ghee", icon: <LocalGroceryStore /> }
    ]
  },
  {
    category: { bangla: "দুধ ও দুগ্ধজাত পণ্য", english: "Milk & Dairy" },
    icon: <Icecream />,
    items: [
      { bangla: "তরল দুধ", english: "Liquid Milk", icon: <Icecream /> },
      { bangla: "পাউডার দুধ", english: "Powder Milk", icon: <Icecream /> },
      { bangla: "দই", english: "Yogurt", icon: <Icecream /> },
      { bangla: "চিজ", english: "Cheese", icon: <Icecream /> },
      { bangla: "বাটার", english: "Butter", icon: <Icecream /> }
    ]
  },
  {
    category: { bangla: "নিত্যপ্রয়োজনীয় পণ্য", english: "Daily Essentials" },
    icon: <ShoppingCart />,
    items: [
      { bangla: "লবণ", english: "Salt", icon: <ShoppingCart /> },
      { bangla: "চিনি", english: "Sugar", icon: <ShoppingCart /> },
      { bangla: "চা পাতা", english: "Tea Leaves", icon: <ShoppingCart /> },
      { bangla: "কফি", english: "Coffee", icon: <ShoppingCart /> },
      { bangla: "ময়দা", english: "Flour", icon: <ShoppingCart /> },
      { bangla: "সুজি", english: "Semolina", icon: <ShoppingCart /> }
    ]
  },
  {
    category: { bangla: "জলপান ও পানীয়", english: "Beverages" },
    icon: <LocalCafe />,
    items: [
      { bangla: "বটল পানি", english: "Bottled Water", icon: <LocalCafe /> },
      { bangla: "সফট ড্রিংকস", english: "Soft Drinks", icon: <LocalCafe /> },
      { bangla: "ফ্রুট জুস", english: "Fruit Juice", icon: <LocalCafe /> },
      { bangla: "এনার্জি ড্রিংকস", english: "Energy Drinks", icon: <LocalCafe /> }
    ]
  },
  {
    category: { bangla: "হাইজিন ও ক্লিনিং", english: "Cleaning & Hygiene" },
    icon: <CleaningServices />,
    items: [
      { bangla: "সাবান", english: "Soap", icon: <CleaningServices /> },
      { bangla: "ডিটারজেন্ট", english: "Detergent", icon: <CleaningServices /> },
      { bangla: "টিস্যু", english: "Tissue", icon: <CleaningServices /> },
      { bangla: "স্যানিটাইজার", english: "Sanitizer", icon: <CleaningServices /> },
      { bangla: "ডিশ ওয়াশ", english: "Dish Wash", icon: <CleaningServices /> }
    ]
  },
  {
    category: { bangla: "বিস্কুট ও স্ন্যাকস", english: "Biscuits & Snacks" },
    icon: <Fastfood />,
    items: [
      { bangla: "বিস্কুট", english: "Biscuits", icon: <Fastfood /> },
      { bangla: "চিপস", english: "Chips", icon: <Fastfood /> },
      { bangla: "চানাচুর", english: "Chanachur", icon: <Fastfood /> },
      { bangla: "চকোলেট", english: "Chocolate", icon: <Fastfood /> },
      { bangla: "প্যাকেট স্ন্যাকস", english: "Packaged Snacks", icon: <Fastfood /> }
    ]
  },
  {
    category: { bangla: "মসলা", english: "Spices" },
    icon: <Whatshot />,
    items: [
      { bangla: "হলুদ গুঁড়া", english: "Turmeric Powder", icon: <Whatshot /> },
      { bangla: "মরিচ গুঁড়া", english: "Chili Powder", icon: <Whatshot /> },
      { bangla: "ধনিয়া গুঁড়া", english: "Coriander Powder", icon: <Whatshot /> },
      { bangla: "গরম মসলা", english: "Mixed Spices", icon: <Whatshot /> },
      { bangla: "জিরা", english: "Cumin", icon: <Whatshot /> }
    ]
  },
 {
    category: { bangla: "মাংস ও মাছ", english: "Meats & Fishes" },
    icon: <SetMeal />,
    items: [
      { bangla: "গরুর মাংস", english: "Beef", icon: <SetMeal /> },
      { bangla: "খাসির মাংস", english: "Mutton", icon: <SetMeal /> },
      { bangla: "মুরগির মাংস", english: "Chicken", icon: <SetMeal /> },
      { bangla: "ডিম", english: "Eggs", icon: <SetMeal /> },
      { bangla: "পাঙ্গাস", english: "Pangas Fish", icon: <SetMeal /> },
      { bangla: "রুই", english: "Rui Fish", icon: <SetMeal /> },
      { bangla: "ইলিশ", english: "Hilsa", icon: <SetMeal /> },
      { bangla: "চিংড়ি", english: "Prawns", icon: <SetMeal /> }
    ]
  },

  {
    category: { bangla: "শাকসবজি", english: "Vegetables" },
    icon: <Grass />,
    items: [
      { bangla: "আলু", english: "Potato", icon: <Grass /> },
      { bangla: "বেগুন", english: "Brinjal", icon: <Grass /> },
      { bangla: "টমেটো", english: "Tomato", icon: <Grass /> },
      { bangla: "পেঁয়াজ", english: "Onion", icon: <Grass /> },
      { bangla: "মূলা", english: "Radish", icon: <Grass /> },
      { bangla: "লাউ", english: "Bottle Gourd", icon: <Grass /> },
      { bangla: "করলা", english: "Bitter Gourd", icon: <Grass /> },
      { bangla: "পালং শাক", english: "Spinach", icon: <Grass /> }
    ]
  },

  {
    category: { bangla: "খেলনা", english: "Toys" },
    icon: <Toys />,
    items: [
      { bangla: "প্লাস্টিক খেলনা", english: "Plastic Toys", icon: <Toys /> },
      { bangla: "শিক্ষামূলক খেলনা", english: "Educational Toys", icon: <Toys /> },
      { bangla: "পুতুল", english: "Dolls", icon: <Toys /> },
      { bangla: "গাড়ি", english: "Toy Cars", icon: <Toys /> },
      { bangla: "বল", english: "Balls", icon: <Toys /> },
      { bangla: "মিউজিকাল খেলনা", english: "Musical Toys", icon: <Toys /> }
    ]
  } 
];



// Nursery categories
const nurseryCategories = [
  {
    category: { bangla: "ফুল গাছ", english: "Flowering Plants" },
    icon: <LocalFlorist />,
    items: [
      { bangla: "গোলাপ", english: "Rose", icon: <LocalFlorist /> },
      { bangla: "গাঁদা", english: "Marigold", icon: <LocalFlorist /> },
      { bangla: "চামেলি", english: "Jasmine", icon: <LocalFlorist /> },
      { bangla: "বেলী", english: "Beli", icon: <LocalFlorist /> },
      { bangla: "হিবিসকাস", english: "Hibiscus", icon: <LocalFlorist /> }
    ]
  },
  {
    category: { bangla: "ফল গাছ", english: "Fruit Trees" },
    icon: <Nature />,
    items: [
      { bangla: "আম", english: "Mango", icon: <Nature /> },
      { bangla: "কাঁঠাল", english: "Jackfruit", icon: <Nature /> },
      { bangla: "লিচু", english: "Litchi", icon: <Nature /> },
      { bangla: "আমরুদ", english: "Guava", icon: <Nature /> },
      { bangla: "পেঁপে", english: "Papaya", icon: <Nature /> }
    ]
  },
  {
    category: { bangla: "সবজি গাছ", english: "Vegetable Plants" },
    icon: <Spa />,
    items: [
      { bangla: "লাউ", english: "Bottle Gourd", icon: <Spa /> },
      { bangla: "পুঁই শাক", english: "Malabar Spinach", icon: <Spa /> },
      { bangla: "টমেটো", english: "Tomato", icon: <Spa /> },
      { bangla: "বেগুন", english: "Brinjal", icon: <Spa /> },
      { bangla: "ঢেঁড়শ", english: "Okra", icon: <Spa /> }
    ]
  },
  {
    category: { bangla: "ওষধি গাছ", english: "Medicinal Plants" },
    icon: <Healing />,
    items: [
      { bangla: "তুলসী", english: "Basil", icon: <Healing /> },
      { bangla: "আলোভেরা", english: "Aloe Vera", icon: <Healing /> },
      { bangla: "নিম", english: "Neem", icon: <Healing /> },
      { bangla: "হলুদ গাছ", english: "Turmeric Plant", icon: <Healing /> },
      { bangla: "অশ্বগন্ধা", english: "Ashwagandha", icon: <Healing /> }
    ]
  },
  {
    category: { bangla: "শোভাময় গাছ", english: "Ornamental Plants" },
    icon: <Park />,
    items: [
      { bangla: "মানিপ্লান্ট", english: "Money Plant", icon: <Park /> },
      { bangla: "ক্রোটন", english: "Croton", icon: <Park /> },
      { bangla: "অর্কিড", english: "Orchid", icon: <Park /> },
      { bangla: "স্নেক প্ল্যান্ট", english: "Snake Plant", icon: <Park /> },
      { bangla: "বনসাই", english: "Bonsai", icon: <Park /> }
    ]
  }
];




// Utility function to generate URL-friendly slugs
const generateSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim();
};

const stores = [
  {
    name: 'ফ্রেশ মার্ট',
    englishName: 'Fresh Mart',
    slug: 'fresh-mart',
    logo: '/images/logos/freshmart.png',
    description: 'তাজা ও মানসম্পন্ন খাবারের জন্য'
  },
  {
    name: 'ডেইলি নিডস স্টোর',
    englishName: 'Daily Needs Store',
    slug: 'daily-needs-store',
    logo: '/images/logos/dailyneeds.png',
    description: 'দৈনন্দিন প্রয়োজনের সম্পূর্ণ সমাধান'
  },
  {
    name: 'সিটি গ্রোসারি',
    englishName: 'City Grocery',
    slug: 'city-grocery',
    logo: '/images/logos/cityg.png',
    description: 'শহরের সবচেয়ে পুরাতন মুদি দোকান'
  },
  {
    name: 'গ্রোসারি ওয়ার্ল্ড',
    englishName: 'Grocery World',
    slug: 'grocery-world',
    logo: '/images/logos/groceryworld.png',
    description: 'আধুনিক মুদি সেবা প্রদানকারী'
  },
  {
    name: 'ফ্রেশ প্লাস',
    englishName: 'Fresh Plus',
    slug: 'fresh-plus',
    logo: '/images/logos/freshplus.png',
    description: '২৪/৭ পণ্য সরবরাহ সেবা'
  },
  {
    name: 'পপুলার গ্রোসারি',
    englishName: 'Popular Grocery',
    slug: 'popular-grocery',
    logo: '/images/logos/popularg.png',
    description: 'জনপ্রিয় ও সাশ্রয়ী পণ্যের দোকান'
  },
  {
    name: 'কুইক মার্ট',
    englishName: 'Quick Mart',
    slug: 'quick-mart',
    logo: '/images/logos/quickmart.png',
    description: 'দ্রুত ডেলিভারি ও অনলাইন সেবা'
  },
  {
    name: 'কর্নার স্টোর',
    englishName: 'Corner Store',
    slug: 'corner-store',
    logo: '/images/logos/corners.png',
    description: 'আপনার এলাকার নির্ভরযোগ্য মুদি দোকান'
  },
];

const groceriesData = [
  {
    id: 1,
    name: 'সরিষার তেল ১লিটার',
    englishName: 'Mustard Oil 1L',
    slug: 'mustard-oil-1l',
    category: 'তেল ও ঘি',
    store: 'ফ্রেশ মার্ট',
    price: '৳১৮০.০০',
    image: '/images/mustardoil.jpg',
    description: 'খাঁটি সরিষার তেল। রান্নার জন্য আদর্শ এবং স্বাস্থ্যকর।',
    brand: 'ফ্রেশ অয়েল'
  },
  {
    id: 2,
    name: 'তরল দুধ ১লিটার',
    englishName: 'Liquid Milk 1L',
    slug: 'liquid-milk-1l',
    category: 'দুধ ও দুগ্ধজাত পণ্য',
    store: 'ডেইলি নিডস স্টোর',
    price: '৳৬৫.০০',
    image: '/images/milk.jpg',
    description: 'তাজা ও পুষ্টিকর তরল দুধ। প্রতিদিনের পুষ্টির জন্য আদর্শ।',
    brand: 'পুরি ডেইরি'
  },
  {
    id: 3,
    name: 'সাদা লবণ ১কেজি',
    englishName: 'White Salt 1kg',
    slug: 'white-salt-1kg',
    category: 'নিত্যপ্রয়োজনীয় পণ্য',
    store: 'সিটি গ্রোসারি',
    price: '৳৩৫.০০',
    image: '/images/salt.jpg',
    description: 'বিশুদ্ধ সাদা লবণ। প্রতিদিনের রান্নার জন্য প্রয়োজনীয়।',
    brand: 'একে সল্ট'
  },
  {
    id: 4,
    name: 'কোকা কোলা ৫০০মিলি',
    englishName: 'Coca Cola 500ml',
    slug: 'coca-cola-500ml',
    category: 'জলপান ও পানীয়',
    store: 'গ্রোসারি ওয়ার্ল্ড',
    price: '৳৩৫.০০',
    image: '/images/coke.jpg',
    description: 'শীতল পানীয়। তৃষ্ণা মেটানোর জন্য আদর্শ।',
    brand: 'কোকা কোলা কোম্পানি'
  },
  {
    id: 5,
    name: 'ডিটারজেন্ট পাউডার ৫০০গ্রাম',
    englishName: 'Detergent Powder 500g',
    slug: 'detergent-powder-500g',
    category: 'হাইজিন ও ক্লিনিং',
    store: 'ফ্রেশ প্লাস',
    price: '৭৫.০০',
    image: '/images/detergent.jpg',
    description: 'কার্যকর ডিটারজেন্ট পাউডার। কাপড় পরিষ্কারের জন্য উত্তম।',
    brand: 'টাইড'
  },
  {
    id: 6,
    name: 'গ্লুকোজ বিস্কুট ১০০গ্রাম',
    englishName: 'Glucose Biscuit 100g',
    slug: 'glucose-biscuit-100g',
    category: 'বিস্কুট ও স্ন্যাকস',
    store: 'পপুলার গ্রোসারি',
    price: '২৫.০০',
    image: '/images/biscuit.jpg',
    description: 'সুস্বাদু গ্লুকোজ বিস্কুট। জলখাবারের জন্য আদর্শ।',
    brand: 'অলিম্পিয়া'
  },
  {
    id: 7,
    name: 'হলুদ গুঁড়া ১০০গ্রাম',
    englishName: 'Turmeric Powder 100g',
    slug: 'turmeric-powder-100g',
    category: 'মসলা',
    store: 'কুইক মার্ট',
    price: '৪৫.০০',
    image: '/images/holudgura.jpg',
    description: 'খাঁটি হলুদ গুঁড়া। রান্নার জন্য প্রয়োজনীয় মসলা।',
    brand: 'রাধুনী'
  },
  {
    id: 8,
    name: 'ছোট মাছ ৫০০গ্রাম',
    englishName: 'Small Fish 500g',
    slug: 'small-fish-500g',
    category: 'মাংস ও মাছ',
    store: 'কর্নার স্টোর',
    price: '১২০.০০',
    image: '/images/smallfish.jpg',
    description: 'তাজা ছোট মাছ। প্রোটিনের উৎকৃষ্ট উৎস।',
    brand: 'ফ্রেশ ফিশ'
  },
  {
    id: 9,
    name: 'আলু ১কেজি',
    englishName: 'Potato 1kg',
    slug: 'potato-1kg',
    category: 'শাকসবজি',
    store: 'ফ্রেশ মার্ট',
    price: '৳৩০.০০',
    image: '/images/potato.jpg',
    description: 'তাজা আলু। বিভিন্ন রান্নার জন্য ব্যবহার্য।',
    brand: 'লোকাল ফার্ম'
  },
  {
    id: 10,
    name: 'শিশুদের খেলনা গাড়ি',
    englishName: 'Toy Car for Kids',
    slug: 'toy-car-for-kids',
    category: 'খেলনা',
    store: 'ডেইলি নিডস স্টোর',
    price: '১৫০.০০',
    image: '/images/toy.jpg',
    description: 'রঙিন খেলনা গাড়ি। শিশুদের বিনোদনের জন্য।',
    brand: 'টয় ল্যান্ড'
  }
];

const Bazar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { storeSlug } = useParams();
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [typingText, setTypingText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingIndex, setTypingIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedStoreTab, setSelectedStoreTab] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showMobilePopup, setShowMobilePopup] = useState(false);
  const { addToCart } = useCart();

  // Typing animation effect (localized)
  const lang = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn';
  const t = lang === 'en' ? bazarEn : 
            lang === 'np' ? bazarNp : 
            lang === 'pk' ? bazarPk : 
            lang === 'bt' ? bazarBt : 
            lang === 'de' ? bazarDe : 
            lang === 'sl' ? bazarSl : 
            lang === 'af' ? bazarAf : 
            lang === 'cn' ? bazarCn : 
            bazarBn;
  const placeholderTexts = t.search.placeholders;

  // Handle URL parameters for store selection
  useEffect(() => {
    if (storeSlug) {
      const store = stores.find(s => s.slug === storeSlug);
      if (store) {
        setSelectedStore(store);
        setSelectedStoreTab(0);
      } else {
        // If store not found, redirect to main page
        navigate('/daily-bazar', { replace: true });
      }
    } else {
      setSelectedStore(null);
      setSelectedStoreTab(0);
    }
  }, [storeSlug, navigate]);

  // Inject keyframes for the blinking cursor animation
  useEffect(() => {
    const cleanup = injectGlobalStyles();
    return cleanup;
  }, []);

  // Typewriter effect for search placeholders
  useEffect(() => {
    const currentPlaceholder = placeholderTexts[placeholderIndex];
    
    // Handle typing or deleting effect
    const handleTyping = () => {
      // If currently deleting text
      if (isDeleting) {
        // Set text to the substring minus the last character
        setTypingText(prev => prev.slice(0, -1));
        setTypingSpeed(50); // Faster speed when deleting
        
        // When text is fully deleted, start typing next placeholder
        if (typingText === '') {
          setIsDeleting(false);
          setPlaceholderIndex((prev) => (prev + 1) % placeholderTexts.length);
          setTypingSpeed(150); // Normal speed for typing
        }
      } 
      // If currently typing text
      else {
        // Add next character to current text
        setTypingText(currentPlaceholder.slice(0, typingText.length + 1));
        setTypingSpeed(150); // Normal speed for typing
        
        // When text is fully typed, pause before deleting
        if (typingText === currentPlaceholder) {
          // Pause at the end of typing before starting to delete
          setTypingSpeed(2000); // Longer pause before deleting
          setIsDeleting(true);
        }
      }
    };
    
    // Set up timer for typing/deleting animation
    const timer = setTimeout(handleTyping, typingSpeed);
    
    // Clean up the timer
    return () => clearTimeout(timer);
  }, [typingText, isDeleting, placeholderIndex, placeholderTexts, typingSpeed]);

  const handleCategoryClick = (categoryKey) => {
    // Toggle expanded state for this category
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const handleSubCategoryClick = (mainCategoryKey, subCategory) => {
    setSelectedCategory(mainCategoryKey);
    setSelectedSubCategory(subCategory);
  };

  const handleStoreClick = (store) => {
    // Navigate to store URL
    navigate(`/stores/${store.slug}`);
  };

  // Get unique categories for the selected store
  const getStoreCategoriesWithCounts = () => {
    if (!selectedStore) return [];
    
    const storeProducts = groceriesData.filter(product => product.store === selectedStore.name);
    const categoryMap = new Map();
    
    // Count products in each category
    storeProducts.forEach(product => {
      const count = categoryMap.get(product.category) || 0;
      categoryMap.set(product.category, count + 1);
    });
    
    // Convert to array and add "All" option
    const categories = [
      { name: 'সকল পণ্য', count: storeProducts.length },
      ...Array.from(categoryMap.entries()).map(([name, count]) => ({ name, count }))
    ];
    
    return categories;
  };

  const handleStoreTabChange = (event, newValue) => {
    setSelectedStoreTab(newValue);
  };

  const handleCloseStore = () => {
    navigate('/daily-bazar');
  };

  const handleMobilePopupOpen = () => {
    setShowMobilePopup(true);
  };

  const handleMobilePopupClose = () => {
    setShowMobilePopup(false);
  };

  const filteredGroceries = groceriesData.filter((grocery) => {
    const matchesCategory = !selectedCategory || 
      (selectedSubCategory ? grocery.category === selectedSubCategory : 
       groceries.find(cat => cat.category.bangla === selectedCategory)?.items.some(item => item.bangla === grocery.category));
    const matchesStore = !selectedStore || grocery.store === selectedStore.name;
    
    // Add store tab filtering
    let matchesStoreTab = true;
    if (selectedStore && selectedStoreTab > 0) {
      const storeCategories = getStoreCategoriesWithCounts();
      const selectedTabCategory = storeCategories[selectedStoreTab]?.name;
      matchesStoreTab = grocery.category === selectedTabCategory;
    }
    
    const matchesSearch = !searchQuery || 
      grocery.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      grocery.englishName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesStore && matchesStoreTab && matchesSearch;
  });

  const handleAddToCart = (product) => {
    console.log('Bazar - Adding product to cart:', product); // Debug log
    console.log('Bazar - Product price:', product.price, 'Type:', typeof product.price); // Debug log
    
    const cartProduct = {
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      store: product.store,
      stock: product.stock || 10 // Default stock if not provided
    };
    
    console.log('Bazar - Cart product object:', cartProduct); // Debug log
    addToCart(cartProduct, 1); // Add 1 quantity by default
  };

  return (
    <>
      <Header />
      <Box sx={{ flexGrow: 1, p: 2, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Search Header Bar */}
        <SearchBarHeader>
          <VideoBackground autoPlay muted loop>
            <source src="/videos/default.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </VideoBackground>
          <SearchOverlay />
          
          {/* Location Selector */}
          <LocationSelector 
            variant="search-bar"
            onLocationChange={(location) => {
              // Optional: Handle location change if needed
              console.log('Location changed:', location);
            }}
          />

          {/* Animated Search Field */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder={typingText}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              mx: { xs: 0, sm: 2 },
              mt: { xs: 1, sm: 0 },
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              borderRadius: 2,
              boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              },
              zIndex: 2,
              flexGrow: 1,
              minWidth: 0,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ 
                    opacity: isDeleting ? 0 : 1, 
                    transition: 'opacity 0.3s',
                    height: '1.2em', 
                    width: '0.15em', 
                    backgroundColor: 'primary.main',
                    display: 'inline-block',
                    animation: 'blink 1s step-end infinite'
                  }}/>
                </InputAdornment>
              )
            }}
          />
        </SearchBarHeader>

        {/* Mobile Filter Button */}
        {isMobile && (
          <Button
            variant="contained"
            fullWidth
            onClick={handleMobilePopupOpen}
            sx={{
              mb: 2,
              py: 1.5,
              background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
              color: 'white',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: 3,
              '&:hover': {
                background: 'linear-gradient(45deg, #4a2496 30%, #1565c0 90%)',
                boxShadow: 6,
              },
            }}
            startIcon={<Search />}
          >
            {t.sidebar.categoriesTitle} & {t.sidebar.storesTitle}
          </Button>
        )}

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Sidebar */}
          <Box sx={{ 
            width: { xs: '100%', md: '260px' }, 
            minWidth: { md: '260px' },
            pr: { md: 2 }, 
            mb: { xs: 2, md: 0 },
            display: { xs: 'none', md: 'block' }
          }}>
            <Sidebar elevation={3}>
              <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionHeader>
                  <LocalMall sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    {t.sidebar.categoriesTitle}
                  </Typography>
                </SectionHeader>
                <CategoryList sx={{ pl: 1, pr: 1 }}>
                  {(routerLocation.pathname.startsWith('/nursery-bazar') ? nurseryCategories : groceries).map((categoryData, index) => {
                    const categoryKey = categoryData.category.bangla;
                    return (
                      <React.Fragment key={categoryKey}>
                        <ListItem
                          button
                          onClick={() => handleCategoryClick(categoryKey)}
                          selected={selectedCategory === categoryKey}
                          sx={{ py: 1 }}
                        >
                          <ListItemIcon>
                            {categoryData.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={categoryData.category.bangla}
                            secondary={categoryData.category.english}
                            primaryTypographyProps={{ 
                              style: { 
                                fontWeight: selectedCategory === categoryKey ? 'bold' : 'normal',
                                fontSize: '0.9rem'
                              } 
                            }}
                            secondaryTypographyProps={{ 
                              style: { 
                                fontSize: '0.75rem',
                                color: 'text.secondary'
                              } 
                            }}
                          />
                          {expandedCategories[categoryKey] ? <ExpandLess /> : <ExpandMore />}
                        </ListItem>
                        <Collapse in={expandedCategories[categoryKey]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {categoryData.items.map((item) => (
                              <ListItem
                                button
                                key={`${categoryKey}-${item.bangla}`}
                                sx={{ pl: 4, py: 0.5 }}
                                selected={selectedCategory === categoryKey && selectedSubCategory === item.bangla}
                                onClick={() => handleSubCategoryClick(categoryKey, item.bangla)}
                              >
                                <ListItemIcon>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={item.bangla}
                                  secondary={item.english}
                                  primaryTypographyProps={{ 
                                    style: { 
                                      fontSize: '0.8rem',
                                      fontWeight: selectedCategory === categoryKey && selectedSubCategory === item.bangla ? 'bold' : 'normal'
                                    } 
                                  }}
                                  secondaryTypographyProps={{ 
                                    style: { fontSize: '0.7rem' } 
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  })}
                </CategoryList>

                <SectionHeader sx={{ mt: 2 }}>
                  <Store sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    {t.sidebar.storesTitle}
                  </Typography>
                </SectionHeader>
                <StoreList sx={{ pl: 1, pr: 1 }}>
                  {stores.map((store) => (
                    <ListItem
                      button
                      key={store.name}
                      selected={selectedStore?.name === store.name}
                      onClick={() => handleStoreClick(store)}
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 35 }}>
                        <DefaultStoreLogo>
                          <Store sx={{ fontSize: 12, color: '#1976d2' }} />
                        </DefaultStoreLogo>
                      </ListItemIcon>
                      <ListItemText 
                        primary={store.name}
                        secondary={`${store.englishName} • ${store.description}`}
                        primaryTypographyProps={{ 
                          fontSize: '0.875rem',
                          fontWeight: selectedStore?.name === store.name ? 'bold' : 'normal' 
                        }}
                        secondaryTypographyProps={{ 
                          fontSize: '0.75rem',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                </StoreList>
              </Box>
            </Sidebar>
          </Box>

          {/* Main Content */}
          <Box sx={{ 
            width: { xs: '100%', md: 'calc(100% - 260px)' },
            flexGrow: 1 
          }}>
            <MainContent>
              {/* Store Details Section */}
              {selectedStore && (
                <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(45deg, #f8f9fa 30%, #e3f2fd 90%)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      width: 60, 
                      height: 60, 
                      backgroundColor: '#e3f2fd',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #bbdefb',
                      mr: 3
                    }}>
                      <Store sx={{ fontSize: 30, color: '#1976d2' }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography 
                        variant="h5" 
                        component={Link}
                        to={`/stores/${selectedStore.slug}`}
                        sx={{ 
                          fontWeight: 'bold', 
                          color: '#1976d2', 
                          mb: 1,
                          textDecoration: 'none',
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: '#1565c0'
                          },
                          display: 'block'
                        }}
                      >
                        {selectedStore.name}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                        {selectedStore.englishName}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                        {selectedStore.description}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationOn sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" color="text.secondary">
                            {t.store.serviceAreaLabel}: {t.store.nationwide}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocalMall sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" color="text.secondary">
                            {t.store.productCount}: {filteredGroceries.length}{t.common.unitCount}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleCloseStore}
                      startIcon={<Close />}
                      sx={{ ml: 2 }}
                    >
                      {t.buttons.close}
                    </Button>
                  </Box>
                  <Typography variant="h6" sx={{ color: '#1976d2', mb: 1 }}>
                    {selectedStore.name} {t.store.productsOfSuffix}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t.store.description}
                  </Typography>
                  
                  {/* Category Tabs */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                    <Tabs 
                      value={selectedStoreTab} 
                      onChange={handleStoreTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        '& .MuiTab-root': {
                          minWidth: 'auto',
                          px: 2,
                          py: 1,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#1976d2',
                        }
                      }}
                    >
                      {getStoreCategoriesWithCounts().map((category, index) => (
                        <Tab 
                          key={index}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                                {category.name}
                              </Typography>
                              <Box sx={{ 
                                backgroundColor: selectedStoreTab === index ? '#1976d2' : '#e0e0e0',
                                color: selectedStoreTab === index ? 'white' : '#666',
                                borderRadius: '12px',
                                px: 1,
                                py: 0.25,
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                minWidth: '20px',
                                textAlign: 'center'
                              }}>
                                {category.count}
                              </Box>
                            </Box>
                          }
                          sx={{
                            color: selectedStoreTab === index ? '#1976d2' : 'text.secondary',
                            '&.Mui-selected': {
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }
                          }}
                        />
                      ))}
                    </Tabs>
                  </Box>
                </Paper>
              )}

              {/* Grocery Grid */}
              {filteredGroceries.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)', // Mobile portrait: 1 card per row
                      sm: 'repeat(2, 1fr)', // Mobile horizontal: 2 cards per row
                      md: 'repeat(5, 1fr)', // Laptop: 5 cards per row
                      lg: 'repeat(6, 1fr)', // Big screen: 6 cards per row
                      '@media (min-width: 1980px)': {
                        gridTemplateColumns: 'repeat(9, 1fr)' // Extra large screens: 9 cards per row
                      }
                    },
                    width: '100%',
                  }}
                >
                  {filteredGroceries.map((product) => (
                    <Box key={product.id} sx={{ display: 'flex', width: '100%' }}>
                      <GroceryCard sx={{ width: '100%' }}>
                        <Box sx={{ position: 'relative' }}>
                          <CardMedia
                            component={Link}
                            to={`/${generateSlug(selectedStore?.name || 'daily-bazar')}/product/${product.slug || product.id}`}
                            sx={{ 
                              height: 160,
                              objectFit: 'contain', 
                              backgroundColor: '#f5f5f5', 
                              pt: 1,
                              cursor: 'pointer',
                              display: 'block'
                            }}
                          >
                            <img
                              src={product.image}
                              alt={product.name}
                              style={{ 
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                              onError={(e) => {
                                e.target.src = '/images/placeholder.png';
                              }}
                            />
                          </CardMedia>
                          <StyledBadge>{product.category}</StyledBadge>
                        </Box>
                        <CardContent sx={{ 
                          flexGrow: 1, 
                          display: 'flex', 
                          flexDirection: 'column',
                          height: '100%',
                          pb: '16px !important' // Override default padding bottom
                        }}>
                          <Box>
                            <Typography
                              component={Link}
                              to={`/${generateSlug(selectedStore?.name || 'daily-bazar')}/product/${product.slug || product.id}`}
                              gutterBottom
                              variant="h6"
                              sx={{ 
                                textDecoration: 'none',
                                color: 'text.primary',
                                '&:hover': {
                                  color: 'primary.main',
                                },
                                cursor: 'pointer'
                              }}
                            >
                              {product.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {product.englishName}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.primary">
                                <strong>ব্র্যান্ড:</strong> {product.brand}
                              </Typography>
                              <Typography variant="body2" color="text.primary">
                                <strong>দোকান:</strong> {product.store}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ 
                            mt: 'auto', // Push this box to the bottom
                            pt: 2 // Add some spacing from the content above
                          }}>
                            <Typography variant="h6" color="primary" sx={{ mb: 1, textAlign: 'center' }}>
                              {formatPriceWithBengaliDigits(product.price)}
                            </Typography>
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              gap: 1
                            }}>
                              <Button
                                component={Link}
                                to={`/${generateSlug(selectedStore?.name || 'daily-bazar')}/product/${product.slug || product.id}`}
                                variant="contained"
                                color="primary"
                                size="small"
                                fullWidth
                              >
                                {t.buttons.details}
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                fullWidth
                                onClick={() => handleAddToCart(product)}
                              >
                                {t.buttons.addToCart}
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </GroceryCard>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                  <LocalGroceryStore sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t.empty.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t.empty.subtitle}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    sx={{ mt: 2 }}
                    onClick={() => {
                      setSelectedCategory('');
                      setSelectedSubCategory('');
                      setSelectedStore(null);
                      setSelectedStoreTab(0);
                      setSearchQuery('');
                    }}
                  >
                    {t.buttons.clearFilters}
                  </Button>
                </Paper>
              )}
            </MainContent>
          </Box>
        </Box>
      </Box>
      {/* Mobile Filter Popup */}
      <Dialog
        open={showMobilePopup}
        onClose={handleMobilePopupClose}
        fullScreen
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6">
            {t.sidebar.categoriesTitle} & {t.sidebar.storesTitle}
          </Typography>
          <IconButton
            onClick={handleMobilePopupClose}
            sx={{ color: 'white' }}
          >
            ✕
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {/* Search Section */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t.search.title || 'Search'}
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={typingText}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.92)',
                borderRadius: 2,
                boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {/* Categories Section */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t.sidebar.categoriesTitle}
            </Typography>
            <List sx={{ maxHeight: '40vh', overflow: 'auto' }}>
              {(routerLocation.pathname.startsWith('/nursery-bazar') ? nurseryCategories : groceries).map((categoryData, index) => {
                const categoryKey = categoryData.category.bangla;
                return (
                  <React.Fragment key={categoryKey}>
                    <ListItem
                      button
                      onClick={() => {
                        handleCategoryClick(categoryKey);
                        handleMobilePopupClose();
                      }}
                      selected={selectedCategory === categoryKey}
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon>
                        {categoryData.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={categoryData.category.bangla}
                        secondary={categoryData.category.english}
                        primaryTypographyProps={{ 
                          style: { 
                            fontWeight: selectedCategory === categoryKey ? 'bold' : 'normal',
                            fontSize: '0.9rem'
                          } 
                        }}
                        secondaryTypographyProps={{ 
                          style: { 
                            fontSize: '0.75rem',
                            color: 'text.secondary'
                          } 
                        }}
                      />
                      {expandedCategories[categoryKey] ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>
                    <Collapse in={expandedCategories[categoryKey]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {categoryData.items.map((item) => (
                          <ListItem
                            button
                            key={`${categoryKey}-${item.bangla}`}
                            sx={{ pl: 4, py: 0.5 }}
                            selected={selectedCategory === categoryKey && selectedSubCategory === item.bangla}
                            onClick={() => {
                              handleSubCategoryClick(categoryKey, item.bangla);
                              handleMobilePopupClose();
                            }}
                          >
                            <ListItemIcon>
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText 
                              primary={item.bangla}
                              secondary={item.english}
                              primaryTypographyProps={{ 
                                style: { 
                                  fontSize: '0.8rem',
                                  fontWeight: selectedCategory === categoryKey && selectedSubCategory === item.bangla ? 'bold' : 'normal'
                                } 
                              }}
                              secondaryTypographyProps={{ 
                                style: { fontSize: '0.7rem' } 
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>
          </Box>

          {/* Stores Section */}
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t.sidebar.storesTitle}
            </Typography>
            <List sx={{ maxHeight: '30vh', overflow: 'auto' }}>
              {stores.map((store) => (
                <ListItem
                  button
                  key={store.name}
                  selected={selectedStore?.name === store.name}
                  onClick={() => {
                    handleStoreClick(store);
                    handleMobilePopupClose();
                  }}
                  sx={{ py: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 35 }}>
                    <DefaultStoreLogo>
                      <Store sx={{ fontSize: 12, color: '#1976d2' }} />
                    </DefaultStoreLogo>
                  </ListItemIcon>
                  <ListItemText 
                    primary={store.name}
                    secondary={`${store.englishName} • ${store.description}`}
                    primaryTypographyProps={{ 
                      fontSize: '0.875rem',
                      fontWeight: selectedStore?.name === store.name ? 'bold' : 'normal' 
                    }}
                    secondaryTypographyProps={{ 
                      fontSize: '0.75rem',
                      color: 'text.secondary'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
      </Dialog>

      <Box sx={{ mt: "50px" }}>
        <Footer />
      </Box>
    </>
  );
};

export default Bazar;
